import { useRef, useState, useEffect } from 'react'
import { AiOutlineInfoCircle } from 'react-icons/ai'
import { FiChevronDown, FiCheck, FiArrowUpRight } from 'react-icons/fi'
import { GTooltip } from '../../../../../../components/basic-blocks'
import { AdSpendDropdown } from '../../../../../../components/ad-spend-dropdown'
import { PlanConfigProps } from './types'

export const PlanConfig = ({
	adSpend,
	onAdSpendChange,
	selectedPlan,
	plans,
	onPlanChange,
	onCompareAll,
	recommendedPlanId
}: PlanConfigProps) => {
	const [dropdownOpen, setDropdownOpen] = useState(false)
	const dropdownRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node)
			) {
				setDropdownOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	return (
		<div className="flex flex-col w-full">
			{/* Dropdowns row */}
			<div className="flex flex-col xl:flex-row gap-[12px]">
				{/* Ad Spend Dropdown */}
				<div className="flex-1 min-w-0 flex flex-col gap-[10px]">
					<div className="flex items-center gap-[4px]">
						<span className="font-grotesk font-medium text-[14px] leading-[18px] text-t-google-ad">
							Your monthly ad spend (USD)
						</span>
						<GTooltip
							position="top"
							content="Total monthly ad spend across all ad accounts you connect to your workspace."
							contentClassName="flex flex-col align-start align-self-stretch border-none bg-t-coupon text-white w-[200px] text-xs p-2 rounded-lg"
						>
							<AiOutlineInfoCircle className="w-3 h-3 text-t-google-ad cursor-pointer" />
						</GTooltip>
					</div>
					<AdSpendDropdown
						value={adSpend}
						onChange={onAdSpendChange}
						showCustomInput={false}
						fontSize="text-[18px]"
					/>
				</div>

				{/* Protection Plan Dropdown */}
				<div className="flex-1 min-w-0 flex flex-col gap-[10px]">
					<span className="font-grotesk font-medium text-[14px] leading-[18px] text-t-google-ad">
						Protection plan
					</span>
					<div ref={dropdownRef} className="relative">
						<button
							type="button"
							onClick={() => setDropdownOpen(!dropdownOpen)}
							className={`w-full bg-white border ${
								dropdownOpen ? 'border-[#554BFD]' : 'border-t-gray-input'
							} rounded-[8px] h-[46px] px-[12px] flex items-center gap-[8px] text-left cursor-pointer drop-shadow-[0px_1px_1px_rgba(16,24,40,0.05)]`}
						>
							<div className="flex-1 flex items-center gap-[8px] min-w-0 justify-between">
								<span className="font-grotesk text-[18px] leading-[24px] text-t-coupon whitespace-nowrap overflow-hidden text-ellipsis">
									{selectedPlan.name}
								</span>
								{selectedPlan.id === recommendedPlanId && (
									<span className="bg-t-step-inactive border border-brand-tint-border text-brand-tint-text text-[10px] font-inter font-medium px-[8px] rounded-full leading-[20px] whitespace-nowrap">
										Recommended
									</span>
								)}
							</div>
							<FiChevronDown
								className={`w-4 h-4 text-t-gray-hold flex-shrink-0 transition-transform ${
									dropdownOpen ? 'rotate-180' : ''
								}`}
							/>
						</button>

						{dropdownOpen && (
							<div className="absolute top-[48px] left-0 w-full bg-popover border border-card-border rounded-[8px] shadow-dropShadow z-20 overflow-hidden p-[6px]">
								{plans.map((plan) => {
									const isSelected = selectedPlan.id === plan.id
									const isDisabled = !!plan.disabled

									return (
										<div key={plan.id} className="px-1.5 pr-4 py-px">
											<button
												type="button"
												onClick={() => {
													if (!isDisabled) {
														onPlanChange(plan.id)
														setDropdownOpen(false)
													}
												}}
												className={`w-full px-2 py-2.5 text-left flex items-center gap-2 rounded-md cursor-pointer ${
													isSelected ? 'bg-popover-hover' : 'hover:bg-popover-hover'
												} ${isDisabled ? 'cursor-default' : ''}`}
											>
												{isSelected && (
													<FiCheck className="w-5 h-5 text-brand-tint-text flex-shrink-0" />
												)}
												<span
													className={`font-grotesk font-medium text-[16px] leading-[24px] ${
												isDisabled ? 'text-t-gray-input' : 'text-t-coupon'
													}`}
												>
													{plan.name}
												</span>
												{plan.id === recommendedPlanId && !isDisabled && (
													<span className="bg-t-step-inactive border border-brand-tint-border text-brand-tint-text text-[10px] font-inter font-medium px-[8px] rounded-full leading-[20px] whitespace-nowrap ml-auto">
														Recommended for you
													</span>
												)}
												{isDisabled && plan.disabledReason && (
													<span className="bg-[color:var(--badge-amber-bg,rgba(255,247,146,0.25))] border border-[var(--badge-amber-border,rgba(250,188,4,0.5))] text-[color:var(--badge-amber-ink,#D9A50B)] text-[10px] font-inter font-medium px-[8px] rounded-full leading-[20px] whitespace-nowrap ml-auto">
														{plan.disabledReason}
													</span>
												)}
											</button>
										</div>
									)
								})}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Recommended microcopy + Compare all plans */}
			<div className="flex items-center gap-[8px]">
				<div className="flex-1 min-w-0 flex items-center gap-[4px]">
					{selectedPlan.id === recommendedPlanId && (
						<p className="font-grotesk text-[14px] leading-[18px] xl:flex xl:items-center gap-[4px]">
							<span className="text-brand-tint-text">{selectedPlan.name} plan </span>
							<span className="text-t-desc-gray">
								recommended based on assessed needs and ad spend
							</span>
						</p>
					)}
				</div>
				<button
					type="button"
					onClick={onCompareAll}
					className="md:flex hidden items-center gap-[8px] px-[12px] py-[8px] rounded-[8px] min-h-[36px] bg-white shrink-0 cursor-pointer hover:bg-gray-100 transition-colors"
				>
					<span className="font-grotesk font-medium text-[14px] leading-[18px] text-t-base-blue whitespace-nowrap">
						Compare all plans
					</span>
					<FiArrowUpRight className="w-4 h-4 text-t-base-blue" />
				</button>
			</div>
		</div>
	)
}
